import { dropTask } from 'ember-concurrency';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import CompanySettingModel from 'tio-common/models/company-setting';
import Component from '@glimmer/component';
import emailSchema from 'tio-common/validation-schema/models/email';
import ENV from 'tio-employee/config/environment';
import formatSubdomainRedirectUrl from 'tio-common/utils/format-subdomain-redirect-url';
import { parseHostname } from 'tio-common/utils/hostname';
import Input from 'tio-reimagined/components/input';
import LoginHere from '../login-here';
import type { IntlService } from 'ember-intl';
import type { NotificationsService } from 'tio-reimagined/components/notifications';
import type PreRegisterService from '../../../services/pre-register.ts';
import type RouterService from '@ember/routing/router-service';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type Owner from '@ember/owner';
import Button from 'tio-reimagined/components/button';
import { safeParse } from 'valibot';
import { timeZone } from 'tio-common/utils/date/time-zone';
import { generateRequestId } from 'tio-common/utils/request-id';

interface AuthRegisterEmployeeRegisterFormComponentSignature {
  Args: {
    settings: CompanySettingModel;
    email: string;
  };
}

export default class AuthRegisterEmployeeRegisterFormComponent extends Component<AuthRegisterEmployeeRegisterFormComponentSignature> {
  @service declare store: StoreService;
  @service declare preRegister: PreRegisterService;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;

  @tracked email: string;
  @tracked isLoading = false;

  constructor(owner: Owner, args: AuthRegisterEmployeeRegisterFormComponentSignature['Args']) {
    super(owner, args);
    this.email = args.email;
  }

  get company() {
    return this.preRegister.sparseCompany;
  }

  get validationErrors() {
    const result = safeParse(emailSchema, { email: this.email });
    return result.success ? [] : result.issues.map((issue) => issue.message);
  }

  get companyName() {
    // @ts-expect-error: seems like a bug
    return this.args.settings.companyName || '';
  }

  get registerPageCopy() {
    return this.args.settings.registerPageCopy;
  }

  registerTask = dropTask(async () => {
    // @ts-expect-error - Ember Data adapter property not typed on MinimumAdapterInterface
    const { host } = this.store.adapterFor('application');

    const response = await fetch(`${host}/preregister`, {
      method: 'POST',
      body: JSON.stringify({ email: this.email, company_id: this.company?.id || null }),
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': ENV.apiKey,
        'Time-Zone': timeZone(),
        'X-Request-Id': generateRequestId(),
      },
    });

    if (response.ok) {
      const res = await response.json();
      if (res.sub_domain) {
        const redirectUrl = formatSubdomainRedirectUrl({
          subDomain: res.sub_domain,
          domain: parseHostname(window.location.hostname).domain,
          email: this.email,
          route: 'register',
        });

        window.location.href = redirectUrl;
      } else {
        this.router.transitionTo('register.confirmation');
      }
    } else {
      const res = await response.json();
      this.notifications.add(res.error || 'An Error Occurred', {
        appearance: 'error',
      });
    }
    this.isLoading = false;
  });

  get isDisabled() {
    return !this.email || this.validationErrors.length > 0;
  }

  submit = (e?: Event) => {
    e?.preventDefault();
    this.isLoading = true;
    this.registerTask.perform();
  };

  <template>
    <form {{on "submit" this.submit}}>
      {{#if this.registerPageCopy}}
        <h2 class="text-xl my-4">
          {{this.registerPageCopy}}
        </h2>
      {{else}}
        <div class="flex flex-col items-center">
          <h2 class="text-2xl my-2">
            {{t "login.hello_and_welcome"}}
          </h2>
          <h2 class="text-lg mb-6 text-center">{{t "login.what_email"}}</h2>
        </div>
      {{/if}}
      <div class="mb-10">
        <Input
          data-test-id="email-input"
          @label={{t "login.email"}}
          @value={{this.email}}
          @hint={{t "login.email_redirect"}}
          @onChange={{fn (mut this.email)}}
          @errors={{this.validationErrors}}
          @type="email"
        />
      </div>

      <div class="flex flex-col items-center">
        <Button
          @intent="primary"
          {{on "click" this.submit}}
          @isRunning={{this.isLoading}}
          @isDisabled={{this.isDisabled}}
          class="w-36 mb-10"
        >
          {{t "common.submit"}}
        </Button>
      </div>
      <LoginHere />
    </form>
  </template>
}
